/**
 * Cloud-VM GUI parity — 3c (docs/cloud-vm/gui-parity-plan.md + agent-activity
 * -journal-tdd.md §6). Projects the durable agent activity journal
 * (`agent.history@1`'s `AgentHistoryPage.items`) into renderer {@link ChatMessage}s,
 * so a cloud Furnace build survives a full browser reload: the chat re-hydrates
 * from the journal instead of a blank surface.
 *
 * In cookie/cloud mode `chat:load` is NOT runtime-served (the chat-history.json
 * IPC path is desktop-only), so the journal IS the canonical hydration source
 * there. The projection mirrors how the live `agent:stream` reducer builds
 * messages (same id/role/turnId/toolCalls/streamStatus shape), and crucially
 * carries each journal line's envelope `id` onto the produced message — the Pin
 * #1 shared-id invariant — so a message recovered from the journal de-dupes
 * cleanly against the same message arriving live (merge-by-id in the loader).
 *
 * This module is PURE + dependency-light: it takes the wire events (validated
 * structurally, unknown kinds ignored) and returns messages. It never throws —
 * a malformed line is skipped, mirroring the reader's graceful-degradation.
 */
import type { InlineCheckpoint } from '@volley/crucible-ipc-types/ipc-channels';
import type { ChatMessage, MessageBlock, ToolCallEntry } from './types';
import { checkpointToChatMessage } from './state';
import { findSupersededTimelineRows } from './turn-dedupe';
import type { JournalOrderKey } from './journal-order-key';

/**
 * Structural view of one journal line as it arrives over the wire from
 * `agent.history@1` (the crucible `AgentJournalEvent` shape, kept decoupled —
 * crucible-ui does not depend on @volley/crucible). Only the fields the
 * projection reads are declared; everything else rides along ignored.
 */
export interface JournalEventLike {
  readonly t?: unknown;
  readonly id?: unknown;
  /** Substrate device attribution — the second component of the journal total
   *  order `(t, device_id, seq, id)` (#3528 LEG 3). Read only to stamp a
   *  projected row's order key; absent/non-string degrades to `''`. */
  readonly device_id?: unknown;
  /** Substrate per-`(device_id, file)` monotonic sequence — the third component
   *  of the journal total order (#3528 LEG 3). Absent/non-number degrades to 0. */
  readonly seq?: unknown;
  readonly kind?: unknown;
  readonly turnId?: unknown;
  /** Run correlation root — one per `agent.send@1`/plan-gate dispatch, stable
   *  across the run's turns. #2949: the fallback accretion key when a
   *  turn-scoped line's `turnId` never materialised (server-initiated runs). */
  readonly runId?: unknown;
  /** Sub-agent attribution from the envelope (full-history, 2026-06): tags a
   *  text/thinking/tool line produced inside a Task worker. */
  readonly agentId?: unknown;
  readonly payload?: unknown;
}

/** The `agent.history@1` result page (only the fields the client reads).
 *  The canonical field is `items` (runtime `AgentHistoryPage.items`, #2035).
 *  `events` is accepted as a legacy/defensive fallback only — the prod RPC has
 *  never returned it, and reading the wrong name silently projected NOTHING,
 *  which is what made every detached/reattached build render blank. */
export interface AgentHistoryPageLike {
  readonly items?: readonly JournalEventLike[];
  readonly events?: readonly JournalEventLike[];
  /**
   * #3528 (LEG 2) — the count of corrupt/unparseable journal lines the read
   * backing this page skipped (the reader's corrupt-line tolerance). When
   * `> 0`, the cold read is DEGRADED: a previously-projected line may have gone
   * unreadable, so the page is NOT a complete snapshot of row absence and the
   * client must fall its authoritative cold-heal closed (retain, never delete).
   * Absent (old pod that predates the field) reads as `0` — same optimistic
   * posture as before the field existed.
   */
  readonly parseErrors?: number;
}

/** crucible#2956 (Codex r6) — options for {@link projectAgentHistoryToChatMessages}. */
export interface ProjectAgentHistoryOptions {
  /**
   * Authoritative agent liveness from `agent:get-status`. When explicitly
   * `false`, a turn with NO terminal line is stamped terminal (`error`) rather
   * than left `in-flight` — the agent is idle, so an unterminated turn is a
   * stalled run: its terminal was withheld (the r5 fail-closed path), dropped,
   * or lost to a pod kill. Left `in-flight` it renders a running assistant/tool
   * turn forever, across reloads. `true`/`undefined` preserve `in-flight` (a
   * genuinely live turn captured mid-build — the reload-survival case).
   */
  readonly agentInFlight?: boolean;
}

/** Placeholder answer text for a stalled turn that produced no text of its own,
 *  so it is not dropped as an empty assistant and the user sees why it stopped. */
const STALLED_TURN_CONTENT =
  'This turn did not finish — the agent is no longer running and no terminal was recorded ' +
  '(the build may have been interrupted, or the result was lost).';

/** Output stamped onto a still-`running` tool card when its turn is terminalised
 *  as stalled — so the card stops spinning and reads as unresolved, not pending. */
const STALLED_TOOL_OUTPUT =
  'No result recorded — the agent stopped before this tool call completed.';

function asString(v: unknown): string | undefined {
  return typeof v === 'string' ? v : undefined;
}

/**
 * #3528 (LEG 3) — build the journal total-order key `(t, device_id, seq, id)`
 * of the line that mints a projected row. `t` is the raw ISO string (matching
 * the substrate's string compare); `id` is the envelope id (equals the produced
 * `ChatMessage.id`).
 *
 * #3528 (LEG 3, codex finding 5) — FAIL CLOSED on a degraded/incomplete key: if
 * any substrate ordering field is missing/wrong-typed (`t`/`device_id` not a
 * string, `seq` not a finite number) the key is UNDEFINED, NOT fabricated
 * (`''`/`0`). A fabricated key sorts earliest and would make a degraded row
 * MORE eligible for deletion (fail-open); returning `undefined` instead routes
 * the row through the reducer's "no order key ⇒ never deletable" guard. Real pod
 * pages always carry all three fields, so a well-formed row always gets a key;
 * this only bites a malformed/legacy page (belt-and-braces).
 */
function journalOrderKeyOf(ev: JournalEventLike, id: string): JournalOrderKey | undefined {
  const t = asString(ev.t);
  const deviceId = asString(ev.device_id);
  const seq = typeof ev.seq === 'number' && Number.isFinite(ev.seq) ? ev.seq : undefined;
  if (t === undefined || deviceId === undefined || seq === undefined) return undefined;
  return { t, deviceId, seq, id };
}

function asRecord(v: unknown): Record<string, unknown> {
  return typeof v === 'object' && v !== null ? (v as Record<string, unknown>) : {};
}

function toMillis(t: unknown): number {
  if (typeof t !== 'string') return 0;
  const ms = Date.parse(t);
  return Number.isFinite(ms) ? ms : 0;
}

function stringifyResult(result: unknown): string {
  if (typeof result === 'string') return result;
  try {
    return JSON.stringify(result);
  } catch {
    return String(result);
  }
}

/**
 * Project an `agent.history@1` page (or a bare events array) into chat messages,
 * ordered by the journal's primary sort key (`t`). One assistant message per
 * `turnId`, accreting `toolCalls` from `tool.call`/`tool.result`/`file.edit`
 * lines; one user message per `turn.user`. A turn whose terminal line is absent
 * (the in-flight turn captured mid-build) still yields an assistant message with
 * `streamStatus: 'in-flight'` carrying the activity so far — that is the whole
 * point on a reload while the agent is working.
 */
export function projectAgentHistoryToChatMessages(
  page: AgentHistoryPageLike | readonly JournalEventLike[] | null | undefined,
  options?: ProjectAgentHistoryOptions,
): ChatMessage[] {
  let events: readonly JournalEventLike[];
  if (Array.isArray(page)) {
    events = page;
  } else if (page && typeof page === 'object') {
    // `agent.history@1` returns the events under `items` (runtime
    // `AgentHistoryPage.items`). Read that first; `events` is a legacy
    // fallback. Reading only `events` (which the RPC never sets) projected an
    // empty timeline on every poll — the root cause of the silent detached
    // build, upstream of the merge/gate fixes.
    const p = page as AgentHistoryPageLike;
    events = (Array.isArray(p.items) ? p.items : Array.isArray(p.events) ? p.events : []) ?? [];
  } else {
    events = [];
  }
  if (events.length === 0) return [];

  // Sort by `t` (the reader's primary sort key) so the projection is stable
  // regardless of per-file/per-device interleaving in the page.
  const sorted = [...events].sort((a, b) => toMillis(a.t) - toMillis(b.t));

  // One assistant message per turnId, built up as activity lines stream past.
  const assistantByTurn = new Map<string, ChatMessage>();
  // callId → the ToolCallEntry we created on tool.call, so tool.result can
  // update it in place.
  const toolCallByCallId = new Map<string, ToolCallEntry>();
  const out: ChatMessage[] = [];
  // Turns for which we saw at least one `turn.text-segment` line. When set, the
  // ordered `blocks` ARE the interleaved play-by-play and `turn.finalised` must
  // NOT prepend a duplicate leading text block — it only stamps `content` +
  // streamStatus. When absent (a pre-full-history journal that only has the
  // concatenated finalText), `turn.finalised` falls back to one leading block.
  const turnsWithSegments = new Set<string>();

  // #2949 (Codex HIGH on PR #2960) — fallback keying for TURNLESS lines. A run
  // is multi-turn (the pod tee resets `turnId` per terminal for repair-loop
  // turns within the same run), so keying every turnId-null line on the bare
  // `runId` would alias distinct turns into ONE assistant message — mixing
  // their text/tool state and collapsing separate user prompts. Instead the
  // fallback key is `run:<runId>:<ordinal>`, and the ordinal advances at turn
  // boundaries: a terminal line closes the current bucket, and a fresh
  // `turn.user` landing in an already-used bucket opens the next one.
  const runFallbackOrdinal = new Map<string, number>();
  const runFallbackUsed = new Set<string>();
  const fallbackTurnKey = (runId: string): string =>
    `run:${runId}:${runFallbackOrdinal.get(runId) ?? 0}`;
  const advanceFallbackTurn = (runId: string): void => {
    runFallbackOrdinal.set(runId, (runFallbackOrdinal.get(runId) ?? 0) + 1);
    runFallbackUsed.delete(runId);
  };
  // Codex round-2 on #2960 (mixed turnId shapes) — the run's currently-OPEN
  // real turn. A turnless straggler in a run whose other lines carry real
  // turnIds must JOIN that open turn, not land in a detached synthetic
  // bucket; a terminal line closes it so later turnless lines can never glue
  // onto a finished turn.
  //
  // Accepted tradeoffs (Codex round-3, both MEDIUM, both pinned by test): no
  // CURRENT producer emits turnless turn-scoped lines at all — the tee stamps
  // turnId before any event — so this whole lane is defence-in-depth for a
  // future/buggy producer, and it optimises for "visible and safe" over
  // perfect attribution: (a) a straggler sorting BEFORE the run's first real
  // line renders as its own detached bubble (no retroactive reparenting of an
  // already-pushed message); (b) a turnless TERMINAL finalises the run's open
  // real turn — the only open turn a sequential-turn producer can have. Both
  // degrade to an extra/mis-attributed VISIBLE bubble, never a silent drop, a
  // cross-turn alias, or a wrong deletion.
  const openRealTurnByRun = new Map<string, string>();

  const ensureAssistant = (turnId: string, ev: JournalEventLike, id: string): ChatMessage => {
    let msg = assistantByTurn.get(turnId);
    if (!msg) {
      msg = {
        id,
        role: 'assistant',
        content: '',
        timestamp: toMillis(ev.t),
        source: 'agent-stream',
        // #3528 — provenance marker: this row was produced by the journal
        // projection, so an authoritative cold merge MAY delete it on absence.
        // See ChatMessage.journalProjected. (`source: 'agent-stream'` alone is
        // NOT journal-exclusive — desktop main mints it too.)
        journalProjected: true,
        // #3528 (LEG 3) — co-stamped with journalProjected: the minting line's
        // full total-order key, so the cold-heal boundary compares the substrate
        // order (t, device_id, seq, id), never `timestamp` (ms) alone.
        journalOrderKey: journalOrderKeyOf(ev, id),
        streamStatus: 'in-flight',
        turnId,
        toolCalls: [],
        // Ordered blocks recovered from the journal. Full-history (2026-06): the
        // journal now carries ordered `turn.text-segment` + `turn.thinking` lines
        // alongside `tool.call`/`file.edit`, so the COMPLETE interleaved play-by-
        // play is reconstructable in `seq` order — text, reasoning, and tool
        // cards thread exactly as they streamed live. A pre-full-history journal
        // (only `turn.finalised.finalText`) falls back to one leading text block.
        // De-dupes against live blocks by the shared envelope id.
        blocks: [],
      };
      assistantByTurn.set(turnId, msg);
      out.push(msg);
    }
    return msg;
  };

  for (const ev of sorted) {
    const kind = asString(ev.kind);
    const id = asString(ev.id);
    if (!kind || !id) continue; // a line with no kind/id is unprojectable
    // #2949 — run/turn correlation for SERVER-initiated (plan-gate) runs: a
    // turn-scoped line whose `turnId` never materialised still carries the
    // run's `runId` on the envelope. Fall back to a per-run ordinal key so the
    // run projects into visible per-turn messages instead of every one of its
    // lines being silently dropped — which rendered the ENTIRE build as a dead
    // chat (live AND after reload), the #2949 failure mode. The ordinal (see
    // fallbackTurnKey above) keeps distinct turnless turns of ONE run separate.
    let turnId = asString(ev.turnId);
    const runId = asString(ev.runId);
    const isTerminalKind =
      kind === 'turn.finalised' || kind === 'turn.errored' || kind === 'turn.interrupted';
    if (runId !== undefined && kind !== 'run.open') {
      if (turnId !== undefined) {
        // Track the run's currently-open REAL turn so a turnless straggler
        // joins it (Codex round-2: mixed turnId shapes). A terminal closes it.
        if (isTerminalKind) openRealTurnByRun.delete(runId);
        else openRealTurnByRun.set(runId, turnId);
        // Bugbot (stale bucket reuse): once real correlation appears, a
        // previously-used synthetic bucket is SEALED — a later turnless line
        // with no open real turn must start a fresh bucket, never glue a new
        // turn's content onto the old detached bubble.
        if (runFallbackUsed.has(runId)) advanceFallbackTurn(runId);
      } else {
        // A turnless user line opens a NEW turn: never glue it (or what
        // follows it) onto the previous turn, real or synthetic.
        if (kind === 'turn.user') {
          openRealTurnByRun.delete(runId);
          if (runFallbackUsed.has(runId)) advanceFallbackTurn(runId);
        }
        const openRealTurn = kind === 'turn.user' ? undefined : openRealTurnByRun.get(runId);
        if (openRealTurn !== undefined) {
          // Straggler joins the run's open real turn.
          turnId = openRealTurn;
          if (isTerminalKind) openRealTurnByRun.delete(runId);
        } else {
          turnId = fallbackTurnKey(runId);
          runFallbackUsed.add(runId);
          // A terminal line ends the fallback turn: it keeps THIS key, and
          // the run's next turnless line lands in a new bucket.
          if (isTerminalKind) advanceFallbackTurn(runId);
        }
      }
    }
    const payload = asRecord(ev.payload);

    switch (kind) {
      case 'turn.user': {
        const text = asString(payload.text) ?? '';
        out.push({
          id,
          role: 'user',
          content: text,
          timestamp: toMillis(ev.t),
          turnId: turnId ?? undefined,
          // #3528 — journal-projection provenance (see ensureAssistant above).
          journalProjected: true,
          // #3528 (LEG 3) — co-stamped total-order key (this turn.user line).
          journalOrderKey: journalOrderKeyOf(ev, id),
        });
        break;
      }
      case 'turn.text-segment': {
        // Full-history (2026-06) — one ordered run of assistant answer text.
        // Append it as a `text` block in journal order so it interleaves with
        // tool / thinking blocks exactly as it streamed. Also accrete into
        // `content` (the legacy concatenation) so a fallback renderer + the
        // markdown-question parser still work.
        if (!turnId) break;
        const text = asString(payload.text);
        if (!text) break;
        const msg = ensureAssistant(turnId, ev, id);
        turnsWithSegments.add(turnId);
        const evAgentId = asString(ev.agentId);
        // A sub-agent's text does NOT accrete into the main `content` blob (the
        // canonical answer text is the main thread's); attributed text lives in
        // blocks only. Main-thread text accretes as before.
        if (!evAgentId) msg.content = (msg.content ?? '') + text;
        msg.blocks = [
          ...(msg.blocks ?? []),
          { kind: 'text', text, ...(evAgentId ? { agentId: evAgentId } : {}) },
        ];
        break;
      }
      case 'turn.thinking': {
        // Full-history (2026-06) — one reasoning block, in journal order. Lives
        // in `blocks` only (never `content`, mirroring the live reducer) so it
        // renders as a dim collapsible segment and never resurrects as answer
        // text.
        if (!turnId) break;
        const text = asString(payload.text);
        if (!text) break;
        const msg = ensureAssistant(turnId, ev, id);
        const evAgentId = asString(ev.agentId);
        msg.blocks = [
          ...(msg.blocks ?? []),
          { kind: 'thinking', text, ...(evAgentId ? { agentId: evAgentId } : {}) },
        ];
        break;
      }
      case 'subagent.start': {
        // Full-history (2026-06) — inline sub-agent dispatch marker, in journal
        // order. The lifecycle line carries the sub-agent id on `agentId`
        // attribution; the payload has the agentType.
        if (!turnId) break;
        const subAgentId = asString(payload.agentId);
        if (!subAgentId) break;
        const msg = ensureAssistant(turnId, ev, id);
        msg.blocks = [
          ...(msg.blocks ?? []),
          {
            kind: 'subagent',
            phase: 'start',
            agentId: subAgentId,
            agentType: asString(payload.agentType) ?? 'sub-agent',
          },
        ];
        break;
      }
      case 'subagent.stop': {
        if (!turnId) break;
        const subAgentId = asString(payload.agentId);
        if (!subAgentId) break;
        const msg = ensureAssistant(turnId, ev, id);
        const stopReason = asString(payload.stopReason);
        msg.blocks = [
          ...(msg.blocks ?? []),
          {
            kind: 'subagent',
            phase: 'stop',
            agentId: subAgentId,
            agentType: asString(payload.agentType) ?? 'sub-agent',
            ...(stopReason === 'success' || stopReason === 'interrupted' || stopReason === 'error'
              ? { stopReason }
              : {}),
          },
        ];
        break;
      }
      case 'turn.finalised': {
        if (!turnId) break;
        const msg = ensureAssistant(turnId, ev, id);
        msg.streamStatus = 'done';
        if (turnsWithSegments.has(turnId)) {
          // Full-history path: the ordered `turn.text-segment` blocks already ARE
          // the answer text in the right places. Keep `content` (accreted from
          // the segments) and do NOT prepend a duplicate leading block.
          const finalText = asString(payload.finalText);
          // Prefer the canonical finalText for `content` when present (it is the
          // authoritative join); the segments already populated blocks.
          if (finalText) msg.content = finalText;
          break;
        }
        // Fallback (pre-full-history journal): only the concatenated finalText
        // exists, so prepend it as ONE leading text block ahead of tool cards.
        const finalText = asString(payload.finalText) ?? msg.content;
        msg.content = finalText;
        if (finalText) {
          msg.blocks = [{ kind: 'text', text: finalText }, ...(msg.blocks ?? [])];
        }
        break;
      }
      case 'turn.errored': {
        if (!turnId) break;
        const msg = ensureAssistant(turnId, ev, id);
        msg.streamStatus = 'error';
        const message = asString(payload.message) ?? 'The agent turn failed.';
        if (!msg.content) msg.content = message;
        break;
      }
      case 'turn.interrupted': {
        if (!turnId) break;
        const msg = ensureAssistant(turnId, ev, id);
        msg.streamStatus = 'interrupted';
        if (!msg.content) msg.content = asString(payload.message) ?? '';
        break;
      }
      case 'tool.call': {
        if (!turnId) break;
        const msg = ensureAssistant(turnId, ev, id);
        const callId = asString(payload.callId) ?? id;
        const entry: ToolCallEntry = {
          id: callId,
          name: asString(payload.toolName) ?? 'tool',
          input: asRecord(payload.toolInput),
          status: 'running',
        };
        toolCallByCallId.set(callId, entry);
        msg.toolCalls = [...(msg.toolCalls ?? []), entry];
        const tcAgentId = asString(ev.agentId);
        msg.blocks = [
          ...(msg.blocks ?? []),
          { kind: 'tool_call', toolCallId: callId, ...(tcAgentId ? { agentId: tcAgentId } : {}) },
        ];
        break;
      }
      case 'tool.result': {
        const callId = asString(payload.callId);
        if (!callId) break;
        const entry = toolCallByCallId.get(callId);
        if (!entry) break; // result with no matching call — skip (out-of-order/partial page)
        entry.status = payload.isError === true ? 'error' : 'success';
        entry.output = stringifyResult(payload.result);
        break;
      }
      case 'file.edit': {
        if (!turnId) break;
        const msg = ensureAssistant(turnId, ev, id);
        msg.toolCalls = [
          ...(msg.toolCalls ?? []),
          {
            id,
            name: asString(payload.tool) ?? 'Edit',
            input: {
              path: asString(payload.path) ?? '',
              operation: asString(payload.operation) ?? 'modify',
            },
            status: 'success',
          },
        ];
        msg.blocks = [...(msg.blocks ?? []), { kind: 'tool_call', toolCallId: id }];
        break;
      }
      case 'activity.checkpoint': {
        // GAP-6 AC#4 — checkpoints are part of the activity timeline, not just
        // chat bubbles. The journal line `id` EQUALS the live `activity:checkpoint`
        // push id (shared-id), and the payload IS the InlineCheckpoint. Build the
        // row with the SAME `checkpointToChatMessage` the live reducer uses, so a
        // reload-restored checkpoint is byte-identical to the live one (and the
        // reducer's id-dedup collapses the two). Skip a malformed payload.
        const cp = payload as unknown as InlineCheckpoint;
        if (typeof cp.id === 'string' && typeof cp.timestamp === 'number') {
          // #3528 — mark the projected checkpoint too, for provenance
          // completeness. (Checkpoints are independently protected from the cold
          // heal by the reducer's `role !== 'inline-checkpoint'` + `checkpoint ===
          // undefined` guards, so this never changes deletion behaviour; the
          // reducer de-dupes a projected checkpoint against its live twin by id.)
          // #3528 (LEG 3) — co-stamp the order key so `journalProjected ⟺
          // journalOrderKey` holds uniformly and the checkpoint contributes its
          // true timeline position to the projection high-water.
          out.push({
            ...checkpointToChatMessage(cp),
            journalProjected: true,
            journalOrderKey: journalOrderKeyOf(ev, id),
          });
        }
        break;
      }
      default:
        // run.open / turn.warning / dev.preview / publish.result / build.* /
        // unknown kinds — not chat-message content.
        break;
    }
  }

  // Drop empty assistant placeholders (a turn that produced no text and no
  // tool activity — nothing for the user to see).
  const projected = out.filter(
    (m) => m.role !== 'assistant' || m.content.length > 0 || (m.toolCalls?.length ?? 0) > 0,
  );

  // crucible#2956 (Codex r6) — authoritative-liveness terminalisation. A turn
  // with no terminal line projects as `in-flight` (the reload-survival case).
  // But when `agent:get-status` reports the agent idle (`agentInFlight === false`)
  // an unterminated turn is NOT live — its terminal was withheld (r5 fail-closed),
  // dropped, or lost to a pod kill (common). Left `in-flight` the surface renders
  // a running assistant/tool turn forever, across reloads. Stamp such turns
  // terminal so hydration reflects reality; resolve still-`running` tool cards so
  // they stop spinning. `true`/`undefined` preserve `in-flight` (a genuinely live
  // turn mid-build). Covers ANY missing-terminal cause, so it is more robust than
  // a runtime durable marker (which the failing journal write can't reliably add).
  if (options?.agentInFlight === false) {
    for (const m of projected) {
      if (m.role !== 'assistant' || m.streamStatus !== 'in-flight') continue;
      m.streamStatus = 'error';
      if (m.toolCalls && m.toolCalls.length > 0) {
        m.toolCalls = m.toolCalls.map((tc) =>
          tc.status === 'running'
            ? { ...tc, status: 'error', output: tc.output ?? STALLED_TOOL_OUTPUT }
            : tc,
        );
      }
      if (!m.content) m.content = STALLED_TURN_CONTENT;
    }
  }

  return projected;
}

/**
 * Merge journal-projected messages into the (possibly empty, in cloud mode)
 * `chat:load` set, de-duped by message `id`. The shared-id invariant (Pin #1)
 * means a turn present in both layers collapses to one message; `chat:load`
 * wins on a conflict (it is the post-save canonical copy). Order follows the
 * combined timeline by `timestamp`, then stable by original index.
 *
 * #2893 Codex P1 — the shared-id invariant does NOT always hold on the desktop
 * cloud path: a turn can arrive here in TWO id-spaces (the chat-history.json rows
 * persisted with renderer ids + the journal projection's pod-id rows). Collapse
 * those twins turn-aware (see turn-dedupe.ts) and keep the journal copy, whose
 * durable ids converge every later merge by plain id. crucible#2902 closed this
 * gap for the common case — the dispatch now forwards the renderer's `turnId`
 * and the pod journal adopts it, so twins share a `turnId` and collapse
 * deterministically; only legacy GUI/pod pairings fall back to the user-text
 * match.
 */
export function mergeHydrationMessages(
  saved: readonly ChatMessage[],
  projected: readonly ChatMessage[],
): ChatMessage[] {
  const superseded = findSupersededTimelineRows(saved, projected, null);
  const projectedById = new Map<string, ChatMessage>();
  for (const m of projected) projectedById.set(m.id, m);
  const byId = new Map<string, ChatMessage>();
  for (const m of saved) {
    if (superseded.has(m.id)) continue;
    // crucible#2956 (Bugbot) — `chat:load` normally wins on an id collision, but a SAVED
    // in-flight assistant row must NOT override a journal projection that has already
    // TERMINALISED the same turn (the agent-idle liveness pass above stamps a missing
    // terminal as `error`). Saved-wins would keep the stale `in-flight` row, so the
    // surface renders a running turn forever across reloads. Prefer the terminalised
    // projection on collision; when the agent is genuinely live the projection stays
    // `in-flight` and this override is inert, so saved still wins.
    const twin = projectedById.get(m.id);
    if (
      twin !== undefined &&
      m.role === 'assistant' &&
      m.streamStatus === 'in-flight' &&
      twin.streamStatus !== 'in-flight'
    ) {
      byId.set(m.id, twin);
    } else {
      byId.set(m.id, m);
    }
  }
  for (const m of projected) if (!byId.has(m.id)) byId.set(m.id, m);
  return [...byId.values()].sort((a, b) => a.timestamp - b.timestamp);
}
